import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { listManagedVenues, type ManagedVenue } from '../../lib/managedVenues';
import { ApiError } from '../../lib/apiClient';
import { useCopy } from '../../copy/CopyProvider';
import './ManagedVenues.css';

/**
 * The Vyrtual-managed venue showcase.
 *
 * Unauthenticated: a promoter looking for a room should not need an account to see one.
 * This is the only public read of venue data in the product. The self-serve directory at
 * /organizer/venues stays authenticated, because those profiles carry pricing structures
 * and technical terms their owners did not publish. A managed room is different: Vyrtual
 * operates it and publishes its rate card deliberately.
 */
export function ManagedVenuesPage() {
  const t = useCopy();
  const [venues, setVenues] = useState<ManagedVenue[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listManagedVenues()
      .then(setVenues)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load venues.'));
  }, []);

  return (
    <div className="mv-page">
      <header className="mv-hero">
        <p className="mv-hero__eyebrow mono">Managed facilities</p>
        <h1 className="mv-hero__title display">
          {t('rentals.browse.heading', 'Book a room we run')}
        </h1>
        <p className="mv-hero__lede">
          {t('rentals.browse.lede', 'Facilities operated by Vyrtual, with production crew, '
            + 'equipment and insurance handled in one booking. Ticketing, access control '
            + 'and settlement run on Voleto.')}
        </p>
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {venues == null && !error && <p className="mv-state">Loading…</p>}
      {venues != null && venues.length === 0 && (
        <p className="mv-state">
          {t('rentals.browse.empty', 'No managed facilities are listed yet.')}
        </p>
      )}

      <div className="mv-grid">
        {(venues ?? []).map((v) => (
          <article className="mv-card" key={v.id}>
            <div className="mv-card__media">
              {v.photos[0]
                ? <img src={v.photos[0]} alt="" />
                : <div className="mv-card__media-fallback" aria-hidden="true" />}
              <span className="mv-card__capacity mono">{v.capacity} capacity</span>
            </div>

            <div className="mv-card__body">
              <h2 className="mv-card__title">{v.name}</h2>
              <p className="mv-card__address">
                {v.address}, {v.city}, {v.state}
              </p>

              <dl className="mv-card__specs">
                {v.stageWidthFt && v.stageDepthFt && (
                  <div>
                    <dt>Stage</dt>
                    <dd className="mono">{v.stageWidthFt} x {v.stageDepthFt} ft</dd>
                  </div>
                )}
                <div>
                  <dt>Operated by</dt>
                  <dd>{v.managingEntity}</dd>
                </div>
              </dl>

              {v.powerSpecs && <p className="mv-card__note">{v.powerSpecs}</p>}

              <Link className="button button--primary button--block" to={`/venues/${v.slug}/book`}>
                Book this facility
              </Link>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
